import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * With the `data-minus-removes` attribute (cart drawer rows), the minus button
 * becomes a remove control at the minimum quantity instead of disabling: the
 * icon swaps to a trash can and a click delegates to the row's remove button,
 * so removal follows the exact same path (animation, optimistic empty state)
 * as the standalone trash.
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * The row's usable remove button, or null when the line can't be removed.
   * @returns {HTMLButtonElement | null}
   */
  #removeButton() {
    const button = this.closest('.cart-items__quantity-controls')?.querySelector('.cart-items__remove');
    return button instanceof HTMLButtonElement && !button.hidden ? button : null;
  }

  /**
   * Whether the minus button currently acts as a remove control.
   * @returns {boolean}
   */
  #inRemoveMode() {
    const { min, value } = this.getCurrentValues();
    return this.hasAttribute('data-minus-removes') && value <= min && this.#removeButton() !== null;
  }

  /**
   * Handles the quantity decrease event; at the minimum, removes the line.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    if (!(event.target instanceof HTMLElement)) return;

    if (this.#inRemoveMode()) {
      event.preventDefault();
      this.#removeButton()?.click();
      return;
    }

    super.decreaseQuantity(event);
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { min, value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();
    const removeMode = this.#inRemoveMode();

    // Cart buttons are always dynamically managed
    minusButton.disabled = value <= min && !removeMode;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;

    this.classList.toggle('quantity-selector--remove-mode', removeMode);
    if (removeMode && minusButton.dataset.removeLabel) {
      minusButton.setAttribute('aria-label', minusButton.dataset.removeLabel);
    } else {
      minusButton.removeAttribute('aria-label');
    }
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
